import type { HTMLAttributes } from 'react';
import { forwardRef, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  createReadableClass,
  customScrollbarMixin,
  getAutoOrCustomSize
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useRect } from 'react-use-rect';
import { Background } from './Background';
import { isTransparentBackground } from './is-transparent-background';
import type { WidgetBackgroundProps } from './types';
import { useImageAnimation } from './useImageAnimation';

export const WidgetBackground = forwardRef<
  HTMLDivElement,
  WidgetBackgroundProps & HTMLAttributes<HTMLDivElement>
>(
  (
    {
      children,
      type,
      color,
      borderWidth,
      borderColor,
      borderRadius,
      horizontalPadding,
      verticalPadding,
      width,
      height,
      imageAnimationEffect,
      triggerImageAnimation = false,
      style,
      className,
      contentStyle,
      wrapperStyle,
      backgroundStyle,
      imageFile,
      containerSlot,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const [containerTop, setContainerTop] = useState(0);
    const [containerRectRef] = useRect((rect) => setContainerTop(rect.width), {
      resize: true
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    const backgroundRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergedRef(
      forwardingRef,
      containerRectRef,
      containerRef
    );

    const hasImage = type === 'image' && !!imageFile?.url;

    const backgroundAnimationStyles = useImageAnimation({
      enabled: hasImage,
      imageAnimationEffect,
      triggerImageAnimation,
      backgroundRef,
      containerRef,
      contentRef
    });

    const isTransparent = isTransparentBackground({ color, type });

    const paddingTop =
      typeof verticalPadding === 'number'
        ? verticalPadding
        : verticalPadding?.[0];

    const paddingBottom =
      typeof verticalPadding === 'number'
        ? verticalPadding
        : verticalPadding?.[1];

    const paddingLeft =
      typeof horizontalPadding === 'number'
        ? horizontalPadding
        : horizontalPadding?.[0];

    const paddingRight =
      typeof horizontalPadding === 'number'
        ? horizontalPadding
        : horizontalPadding?.[1];

    const finalBorderRadius = isTransparent ? 0 : borderRadius;

    // TODO: выпилить настройку ширины, когда все виджеты,
    // использующие этот компонент, будут приведены к нужному виду
    const finalWidth = getAutoOrCustomSize(width);
    const finalHeight = getAutoOrCustomSize(height);

    return (
      <Container
        _height={finalHeight}
        ref={mergedRef}
        className={className}
        style={{
          borderWidth,
          borderColor,
          borderRadius: finalBorderRadius,
          ...style
        }}
      >
        <Wrapper
          _containerHeight={finalHeight}
          style={{
            ...wrapperStyle,
            paddingTop: isTransparent ? 0 : paddingTop,
            paddingRight: isTransparent ? 0 : paddingRight,
            paddingBottom: isTransparent ? 0 : paddingBottom,
            paddingLeft: isTransparent ? 0 : paddingLeft
          }}
        >
          {containerSlot}
          <Background
            {...forwardingProps}
            colorAndImageBlockRef={backgroundRef}
            type={type}
            color={color}
            borderRadius={finalBorderRadius}
            videoOffsetTop={containerTop}
            colorAndImageBlockStyles={backgroundAnimationStyles}
            style={backgroundStyle}
            imageFile={imageFile}
          />
          <Content
            ref={contentRef}
            style={{
              maxWidth: finalWidth,
              ...contentStyle
            }}
          >
            {children}
          </Content>
        </Wrapper>
      </Container>
    );
  }
);

const Container = styled.div.attrs<{
  _height: string | number | undefined;
}>({
  className: createReadableClass({
    component: 'widget-background',
    element: 'container'
  })
})`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  border: 0 solid transparent;
  z-index: 1;
  ${({ _height }) =>
    !!_height &&
    css({ height: _height, overflow: 'hidden auto' }, customScrollbarMixin)}
`;

const Wrapper = styled.div.attrs<{
  _containerHeight: string | number | undefined;
}>({
  className: createReadableClass({
    component: 'widget-background',
    element: 'wrapper'
  })
})`
  display: flex;
  justify-content: center;
  position: relative;
  box-sizing: inherit;
  width: 100%;
  ${({ _containerHeight }) =>
    _containerHeight
      ? css({ minHeight: _containerHeight, height: 'fit-content' })
      : css`
          min-height: 100%;
        `}
`;

const Content = styled.div.attrs({
  className: createReadableClass(
    {
      component: 'widget-background',
      element: 'content'
    },
    ['WidgetBackground__ContentContainer-sc']
  )
})`
  width: 100%;
  z-index: 3;
  // for image animation
  transition: transform 0.3s linear;
`;
